import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import { ArrowLeft, Users, Plus, X, HelpCircle, ShieldCheck } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CreateGroup() {
  const { user, isActiveMember } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    is_public: true,
    requires_approval: false,
    join_questions: []
  });
  const [newQuestion, setNewQuestion] = useState('');

  useEffect(() => {
    if (!isActiveMember) navigate('/membership');
  }, [isActiveMember, navigate]);

  if (!isActiveMember) return null;

  const tier = user?.membership_tier || 'basic';
  const canCreate = tier === 'pro' || tier === 'founder' || user?.role === 'admin';

  if (!canCreate) {
    return (
      <div className="min-h-screen bg-slate-50 py-8" data-testid="create-group-page">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-2xl">
          <Link to="/community" className="inline-flex items-center gap-2 text-slate-600 hover:text-[#3D4F7A] mb-6">
            <ArrowLeft className="w-4 h-4" />Back to Community
          </Link>
          <Card className="border border-slate-200 shadow-lg">
            <CardContent className="p-8 text-center">
              <div className="w-16 h-16 rounded-2xl bg-slate-100 flex items-center justify-center mx-auto mb-4">
                <ShieldCheck className="w-8 h-8 text-slate-400" />
              </div>
              <h2 className="font-heading text-xl font-bold text-slate-900 mb-2">Pro or Founder Membership Required</h2>
              <p className="text-slate-500 mb-6">Group creation is available to Pro and Founder tier members. Upgrade your membership to start building your own groups.</p>
              <Link to="/register">
                <Button className="bg-[#3D4F7A] hover:bg-[#2A3755]">Upgrade Membership</Button>
              </Link>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  const addQuestion = () => {
    if (newQuestion.trim() && formData.join_questions.length < 5) {
      setFormData(prev => ({ ...prev, join_questions: [...prev.join_questions, newQuestion.trim()] }));
      setNewQuestion('');
    }
  };

  const removeQuestion = (index) => {
    setFormData(prev => ({ ...prev, join_questions: prev.join_questions.filter((_, i) => i !== index) }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim() || !formData.description.trim()) {
      toast.error('Please fill in all required fields');
      return;
    }
    setLoading(true);
    try {
      const response = await fetch(`${API}/community/groups`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(formData)
      });
      if (response.ok) {
        const data = await response.json();
        toast.success('Group created! You are the super admin.');
        navigate(`/community/groups/${data.group_id}`);
      } else {
        const err = await response.json();
        toast.error(err.detail || 'Failed to create group');
      }
    } catch (error) {
      toast.error('Failed to create group');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 py-8" data-testid="create-group-page">
      <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-2xl">
        <Link to="/community" className="inline-flex items-center gap-2 text-slate-600 hover:text-[#3D4F7A] mb-6">
          <ArrowLeft className="w-4 h-4" />Back to Community
        </Link>

        <Card className="border border-slate-200 shadow-lg">
          <CardHeader className="text-center">
            <div className="w-16 h-16 rounded-2xl bg-[#3D4F7A] flex items-center justify-center mx-auto mb-4">
              <Users className="w-8 h-8 text-white" />
            </div>
            <CardTitle className="font-heading text-2xl">Create a Group</CardTitle>
            <p className="text-slate-600">Start a community around a topic or interest</p>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="name">Group Name *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="e.g., Luxury Real Estate Professionals"
                  className="h-12"
                  required
                  data-testid="group-name-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">Description *</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="What is this group about? Who should join?"
                  className="min-h-[120px] resize-none"
                  required
                  data-testid="group-description-input"
                />
              </div>

              <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                <div>
                  <Label className="font-medium">Public Group</Label>
                  <p className="text-sm text-slate-500">Anyone can find this group</p>
                </div>
                <Switch
                  checked={formData.is_public}
                  onCheckedChange={(checked) => setFormData(prev => ({ ...prev, is_public: checked }))}
                  data-testid="group-public-switch"
                />
              </div>

              <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                <div>
                  <Label className="font-medium">Require Approval to Join</Label>
                  <p className="text-sm text-slate-500">New members must be approved by you or an admin</p>
                </div>
                <Switch
                  checked={formData.requires_approval}
                  onCheckedChange={(checked) => setFormData(prev => ({ ...prev, requires_approval: checked }))}
                  data-testid="group-approval-switch"
                />
              </div>

              {formData.requires_approval && (
                <div className="space-y-3 p-4 border border-slate-200 rounded-lg bg-white">
                  <div className="flex items-center gap-2">
                    <HelpCircle className="w-4 h-4 text-[#3D4F7A]" />
                    <Label className="font-medium">Join Questions</Label>
                    <span className="text-xs text-slate-400">(optional, up to 5)</span>
                  </div>
                  <p className="text-xs text-slate-500">Ask applicants questions before they can join your group.</p>

                  {formData.join_questions.map((q, i) => (
                    <div key={i} className="flex items-center gap-2 bg-slate-50 rounded-lg px-3 py-2">
                      <span className="text-sm text-slate-700 flex-1">{q}</span>
                      <button type="button" onClick={() => removeQuestion(i)} className="text-red-400 hover:text-red-600" data-testid={`remove-question-${i}`}>
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ))}

                  {formData.join_questions.length < 5 && (
                    <div className="flex gap-2">
                      <Input
                        value={newQuestion}
                        onChange={(e) => setNewQuestion(e.target.value)}
                        placeholder="e.g., What market do you specialize in?"
                        className="flex-1"
                        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addQuestion(); }}}
                        data-testid="new-question-input"
                      />
                      <Button type="button" variant="outline" onClick={addQuestion} data-testid="add-question-btn">
                        <Plus className="w-4 h-4" />
                      </Button>
                    </div>
                  )}
                </div>
              )}

              <Button
                type="submit"
                className="w-full bg-[#3D4F7A] hover:bg-[#2A3755] h-12"
                disabled={loading}
                data-testid="create-group-submit"
              >
                {loading ? 'Creating...' : 'Create Group'}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
